import { useParams, Link } from 'react-router-dom';
import { usePublicProfile, useSellerRaffles, useSellerReviews } from '../hooks/usePublicProfile';
import { formatCurrency, cn } from '../lib/utils';
import { Button, Spinner, Card } from '../components/ui';
import { VerifiedBadge, StatusBadge } from '../components/ui/Badge';
import Avatar from '../components/shared/Avatar';
import RaffleCard from '../components/raffle/RaffleCard';
import EmptyState from '../components/shared/EmptyState';

/**
 * SellerProfile Page
 *
 * Public-facing profile page for sellers.
 * Shows their competitions, reviews, and stats.
 */
export default function SellerProfile() {
  const { userId } = useParams();
  const { profile, loading: profileLoading, error: profileError } = usePublicProfile(userId);
  const { raffles: activeRaffles, loading: activeLoading } = useSellerRaffles(userId, { status: 'active' });
  const { raffles: completedRaffles, loading: completedLoading } = useSellerRaffles(userId, { status: 'completed', limit: 6 });
  const { reviews, breakdown, loading: reviewsLoading } = useSellerReviews(userId, { limit: 5, includeBreakdown: true });

  // Loading state
  if (profileLoading) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Error or not found
  if (profileError || !profile) {
    return (
      <div className="min-h-screen bg-midnight">
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-display font-bold text-cream mb-4">
            Seller Not Found
          </h1>
          <p className="text-cream/60 mb-8">
            This seller profile doesn't exist or has been removed.
          </p>
          <Link to="/browse">
            <Button>Browse Competitions</Button>
          </Link>
        </div>
      </div>
    );
  }

  const memberSince = profile.createdAt?.toDate
    ? profile.createdAt.toDate().toLocaleDateString('en-IE', { month: 'long', year: 'numeric' })
    : 'Unknown';

  return (
    <div className="min-h-screen bg-midnight">
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {/* Profile Header */}
        <div className="bg-midnight-lighter rounded-xl p-6 sm:p-8 mb-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Avatar */}
            <Avatar
              src={profile.photoURL}
              name={profile.displayName}
              size="xl"
              className="w-24 h-24 sm:w-32 sm:h-32 text-2xl"
            />

            {/* Info */}
            <div className="flex-1 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                <h1 className="text-2xl sm:text-3xl font-display font-bold text-cream">
                  {profile.displayName}
                </h1>
                {profile.phoneVerified && <VerifiedBadge size="lg" />}
              </div>

              {/* Rating */}
              {profile.rating ? (
                <div className="flex items-center justify-center sm:justify-start gap-2 mt-2">
                  <StarRating rating={profile.rating} />
                  <span className="text-cream/50">
                    ({profile.reviewCount} review{profile.reviewCount !== 1 ? 's' : ''})
                  </span>
                </div>
              ) : (
                <p className="text-cream/50 mt-2">No reviews yet</p>
              )}

              {/* Bio */}
              {profile.bio && (
                <p className="text-cream/70 mt-4 max-w-xl">{profile.bio}</p>
              )}

              {/* Meta */}
              <div className="flex items-center justify-center sm:justify-start gap-4 mt-4 text-sm text-cream/40">
                <span>Member since {memberSince}</span>
                {profile.location && (
                  <>
                    <span>-</span>
                    <span className="flex items-center gap-1">
                      <LocationIcon className="w-4 h-4" />
                      {profile.location}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="flex gap-6 sm:gap-8">
              <StatBox label="Competitions" value={profile.stats.rafflesCreated} />
              <StatBox label="Rating" value={profile.rating ? profile.rating.toFixed(1) : '-'} />
            </div>
          </div>
        </div>

        {/* Active Competitions */}
        <section className="mb-12">
          <h2 className="text-xl font-display font-bold text-cream mb-4">
            Active Competitions
          </h2>
          {activeLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : activeRaffles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeRaffles.map((raffle) => (
                <RaffleCard key={raffle.id} raffle={raffle} />
              ))}
            </div>
          ) : (
            <Card variant="dark" className="p-8 text-center">
              <p className="text-cream/50">No active competitions</p>
            </Card>
          )}
        </section>

        {/* Completed Competitions */}
        <section className="mb-12">
          <h2 className="text-xl font-display font-bold text-cream mb-4">
            Past Competitions
          </h2>
          {completedLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : completedRaffles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {completedRaffles.map((raffle) => (
                <RaffleCard key={raffle.id} raffle={raffle} />
              ))}
            </div>
          ) : (
            <Card variant="dark" className="p-8 text-center">
              <p className="text-cream/50">No completed competitions yet</p>
            </Card>
          )}
        </section>

        {/* Reviews */}
        <section>
          <h2 className="text-xl font-display font-bold text-cream mb-4">
            Reviews
          </h2>
          {reviewsLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : reviews.length > 0 ? (
            <div className="space-y-6">
              {/* Rating Breakdown */}
              {breakdown && profile.reviewCount > 0 && (
                <RatingBreakdown
                  breakdown={breakdown}
                  totalReviews={profile.reviewCount}
                  averageRating={profile.rating}
                />
              )}

              {/* Individual Reviews */}
              <div className="space-y-4">
                {reviews.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </div>
            </div>
          ) : (
            <Card variant="dark" className="p-8 text-center">
              <p className="text-cream/50">No reviews yet</p>
            </Card>
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * RatingBreakdown - Shows rating distribution (5★: 10, 4★: 5, etc.)
 */
function RatingBreakdown({ breakdown, totalReviews, averageRating }) {
  const maxCount = Math.max(...Object.values(breakdown), 1);

  return (
    <Card variant="dark" className="p-5">
      <div className="flex flex-col sm:flex-row gap-6">
        {/* Average Rating Summary */}
        <div className="flex flex-col items-center justify-center sm:border-r sm:border-cream/10 sm:pr-6">
          <span className="text-4xl font-display font-bold text-cream">
            {averageRating?.toFixed(1) || '-'}
          </span>
          <div className="flex items-center gap-0.5 mt-1">
            {[...Array(5)].map((_, i) => (
              <StarIcon
                key={i}
                className={cn(
                  'w-4 h-4',
                  i < Math.round(averageRating || 0) ? 'text-accent' : 'text-cream/20'
                )}
                filled={i < Math.round(averageRating || 0)}
              />
            ))}
          </div>
          <span className="text-sm text-cream/50 mt-1">
            {totalReviews} review{totalReviews !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Bar Chart */}
        <div className="flex-1 space-y-2">
          {[5, 4, 3, 2, 1].map((rating) => {
            const count = breakdown[rating] || 0;
            const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
            const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0;

            return (
              <div key={rating} className="flex items-center gap-2">
                <span className="text-sm text-cream/70 w-4">{rating}</span>
                <StarIcon className="w-4 h-4 text-accent" filled />
                <div className="flex-1 h-2 bg-midnight rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent rounded-full transition-all duration-300"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
                <span className="text-xs text-cream/50 w-8 text-right">
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

/**
 * StatBox - Displays a single stat
 */
function StatBox({ label, value }) {
  return (
    <div className="text-center">
      <p className="text-2xl font-display font-bold text-cream">{value}</p>
      <p className="text-xs text-cream/40 uppercase tracking-wider">{label}</p>
    </div>
  );
}

/**
 * StarRating - Star display
 */
function StarRating({ rating, maxStars = 5 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(maxStars)].map((_, i) => (
        <StarIcon
          key={i}
          className={cn(
            'w-5 h-5',
            i < Math.round(rating) ? 'text-accent' : 'text-cream/20'
          )}
          filled={i < Math.round(rating)}
        />
      ))}
      <span className="ml-1 text-lg font-mono font-medium text-cream">
        {rating.toFixed(1)}
      </span>
    </div>
  );
}

/**
 * ReviewCard - Single review display
 */
function ReviewCard({ review }) {
  const reviewDate = review.createdAt?.toDate
    ? review.createdAt.toDate().toLocaleDateString('en-IE', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Unknown date';

  return (
    <Card variant="dark" className="p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {/* We don't have reviewer data yet - would need to fetch */}
          <div className="w-10 h-10 rounded-full bg-midnight-lighter flex items-center justify-center text-cream/50">
            <UserIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <StarRatingSmall rating={review.rating} />
            </div>
            {review.text && (
              <p className="text-cream/70 mt-2">{review.text}</p>
            )}
          </div>
        </div>
        <span className="text-xs text-cream/40 whitespace-nowrap">{reviewDate}</span>
      </div>
    </Card>
  );
}

function StarRatingSmall({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <StarIcon
          key={i}
          className={cn(
            'w-4 h-4',
            i < Math.round(rating) ? 'text-accent' : 'text-cream/20'
          )}
          filled={i < Math.round(rating)}
        />
      ))}
    </div>
  );
}

// Icons
function StarIcon({ className, filled = true }) {
  return (
    <svg
      className={className}
      fill={filled ? 'currentColor' : 'none'}
      viewBox="0 0 20 20"
      stroke="currentColor"
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}

function LocationIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function UserIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}
